<template>
  <div class="producao-tab-container">
    <!-- Production history; toggle inside switches between timeline and steps-segmented view -->
    <ProductionHistoryTimeline :logs="historyItems" :steps="steps" />

    <!-- Retroceder etapa (chamado 2026-0027): volta o item para a etapa anterior, com motivo -->
    <div class="retroceder-box" data-testid="retroceder-box">
      <app-button
        v-if="!retrocederAberto"
        :label="t('Retroceder etapa')"
        icon="pi pi-undo"
        severity="warning"
        outlined
        size="small"
        :disabled="!podeRetroceder"
        v-tooltip="podeRetroceder ? t('Volta o item para a etapa anterior') : t('O item ainda está na primeira etapa')"
        data-testid="retroceder-abrir-btn"
        @click="retrocederAberto = true"
      />
      <div v-else class="retroceder-form">
        <input-text-area
          elem-id="retrocederMotivo"
          v-model="retrocederMotivo"
          :label="t('Motivo do retrocesso')"
          :placeholder="t('Explique por que o item volta uma etapa (mínimo 5 caracteres)')"
          :rows="2"
          data-testid="retroceder-motivo"
        />
        <div class="retroceder-actions">
          <app-button :label="t('Cancelar')" text size="small" data-testid="retroceder-cancelar-btn" @click="fecharRetroceder" />
          <app-button
            :label="t('Confirmar retrocesso')"
            icon="pi pi-undo"
            severity="warning"
            size="small"
            :disabled="retrocederMotivo.trim().length < 5 || retrocedendo"
            :loading="retrocedendo"
            data-testid="retroceder-confirmar-btn"
            @click="confirmarRetroceder"
          />
        </div>
      </div>
    </div>

    <!-- Reprocessos -->
    <SOReprocessoSection
      :order-item-id="itemId"
      :completed-steps="completedSteps"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import AppButton from '../../../ui/AppButton.vue';
import InputTextArea from '../../../inputs/InputTextArea.vue';
import { eventBus } from '../../../eventBus';
import { useSOProducao } from '../../../composables/useSOProducao';
import { useSOScanner } from '../../../composables/useSOScanner';
import type { Orders_WorkOrderStepRow } from '../../../contracts/Orders_WorkOrderStepRow';

const { t } = useI18n();

const props = defineProps<{
  itemId: number;
}>();

const { fetchOrderItemSteps, retrocederEtapa } = useSOProducao();
const { fetchProductionHistory } = useSOScanner();

const steps = ref<Orders_WorkOrderStepRow[]>([]);
const historyItems = ref<unknown[]>([]);
const completedSteps = ref<unknown[]>([]);

const retrocederAberto = ref<boolean>(false);
const retrocederMotivo = ref<string>('');
const retrocedendo = ref<boolean>(false);
/** Só há para onde voltar quando existe uma etapa concluída (ou pulada) antes da atual. */
const podeRetroceder = computed<boolean>(() =>
  steps.value.some((s) => s.so_work_order_step_status === 'completed' || s.so_work_order_step_status === 'skipped')
);

const fecharRetroceder = (): void => {
  retrocederAberto.value = false;
  retrocederMotivo.value = '';
};

const confirmarRetroceder = async (): Promise<void> => {
  if (retrocederMotivo.value.trim().length < 5) return;
  retrocedendo.value = true;
  try {
    await retrocederEtapa(props.itemId, retrocederMotivo.value.trim());
    eventBus.emit('globalMessage.success', t('Item retrocedeu uma etapa'));
    fecharRetroceder();
    await loadData();
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    eventBus.emit('globalMessage.error', t('Erro ao retroceder etapa') + ': ' + message);
  } finally {
    retrocedendo.value = false;
  }
};

async function loadData() {
  try {
    const [stepResult, historyResult] = await Promise.all([
      fetchOrderItemSteps(props.itemId),
      fetchProductionHistory(props.itemId),
    ]);

    steps.value = stepResult || [];
    historyItems.value = historyResult || [];

    // Derive completed steps for reprocesso section
    if (stepResult) {
      completedSteps.value = stepResult.filter(
        (s: any) => s.so_work_order_step_status === 'completed'
      );
    }
  } catch (err) {
    // Error handled by composables
  }
}

defineExpose({ loadData });
</script>

<style scoped>
.retroceder-box { display: flex; flex-direction: column; gap: 0.5rem; }
.retroceder-form { display: flex; flex-direction: column; gap: 0.5rem; }
.retroceder-actions { display: flex; gap: 0.5rem; justify-content: flex-end; }
.producao-tab-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
</style>
